import { useState, useEffect, useRef } from "react";
import authApi from "api/auth";
import { useLookupsContext } from "hooks/useContext";
import useNotify from "hooks/useNotify";

function useStartJourney({ setValue = () => {} }) {
  const {
    countries,
    cities,
    domains,
    nationalities,
    languages,
    services,
    timezones,
    getCities,
    getTimeZones,
    dataToBeLoaded,
    getSubDomains,
    subDomains,
  } = useLookupsContext(true);

  const [tempFileId, setTempFileId] = useState(null);
  const [counter, setCounter] = useState(1);
  const intervalRef = useRef(null);

  // Handle location data (country and city)
  const handleLocationData = (country, city) => {
    if (!country) return;

    const countryObj = countries.find((c) => c.name === country);
    if (!countryObj) return;

    setValue("country", countryObj.id);
    getCities(countryObj.id);

    if (city) {
      // Wait for cities to load based on country
      setTimeout(() => {
        const cityObj = cities.find((c) => c.name === city);
        if (cityObj) setValue("city", cityObj.id);
      }, 500);
    }
  };

  // Handle nationality data
  const handleNationalityData = (nationalityArray) => {
    if (!nationalityArray || !Array.isArray(nationalityArray)) return;

    const nationalityIds = nationalityArray
      .map((nat) => nationalities.find((n) => n.name === nat)?.id)
      .filter(Boolean);

    if (nationalityIds.length) setValue("nationalities", nationalityIds);
  };

  // Handle profile links
  const handleProfileLinks = (profileLinks) => {
    if (!profileLinks || !Array.isArray(profileLinks)) return;

    setValue("vendorAdditionalInfo", {
      profile: profileLinks,
    });
  };

  // Reset state and clear interval
  const resetState = () => {
    clearInterval(intervalRef.current);
    setTempFileId(null);
    setCounter(1);
    document.body.style.pointerEvents = "auto";
  };

  const fetchUserData = async () => {
    try {
      const result = await authApi.resumeExtraction(tempFileId);

      if (result?.data) {
        const userData = result.data;

        // Set basic user information
        if (userData.firstName) setValue("firstName", userData.firstName);
        if (userData.lastName) setValue("lastName", userData.lastName);
        if (userData.email) setValue("email", userData.email);
        if (userData.phoneNumber) setValue("phoneNumber", userData.phoneNumber);

        // Handle location data
        handleLocationData(userData.country, userData.city);

        // Handle nationality data
        handleNationalityData(userData.nationality);

        // Set years of experience
        if (userData.yearsOfExperience) {
          setValue("yearsOfExperience", userData.yearsOfExperience);
        }

        // Handle profile links
        handleProfileLinks(userData.profileLinks);

        resetState();
      }
    } catch (err) {
      resetState();
    }
  };

  useEffect(() => {
    if (counter < 7 && tempFileId) {
      clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => {
        fetchUserData();
        setCounter((prevCounter) => prevCounter + 1);
      }, 5000);
    } else {
      resetState();
    }

    return () => {
      clearInterval(intervalRef.current);
    };
  }, [tempFileId, counter]);

  return { tempFileId, setTempFileId, setCounter };
}

export default useStartJourney;
